/**
 * Dependencies.
 */
import * as L from 'leaflet';
import {
  Icon,
  icon,
  LatLngBounds,
  LatLngTuple,
  Map,
  MapOptions,
  Point,
  TileLayerOptions
} from 'leaflet';
import * as LFilter from 'leaflet.tilelayer.colorfilter';
import { isNumber } from 'lodash-es';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { LControl, LMap, LMarker, LTileLayer } from 'vue2-leaflet';
import LMovingRotatedMarker from 'vue2-leaflet-moving-rotated-marker';

import { getBoundsZoom } from '@/modules/main/utils/map';

/**
 * Interface `ICoordinates`.
 */
interface ICoordinates {
  latitude: number;
  longitude: number;
  direction?: number;
}

/**
 * Component.
 */
@Component({
  components: { LControl, LMap, LMarker, LMovingRotatedMarker, LTileLayer },
  name: 'tracking-vehicle-component'
})
export default class extends Vue {
  /**
   * Data `leafletMap`.
   */
  leafletMap: Map = null;

  /**
   * Data `autoFit`.
   */
  autoFit = true;

  /**
   * Property `defaultMarker`.
   */
  defaultMarker = require('@/assets/marker.svg');

  /**
   * Property `initialMarker`.
   */
  initialMarker = require('@/assets/marker-processing.svg');

  /**
   * Property `successMarker`.
   */
  successMarker = require('@/assets/marker-delivered.svg');

  /**
   * Property `unsuccessMarker`.
   */
  unsuccessMarker = require('@/assets/marker-not-delivered.svg');

  /**
   * Property `inServicePointMarker`.
   */
  inServicePointMarker = require('@/assets/marker-in-service-point.svg');

  /**
   * Property `onTheWayToServicePointMarker`.
   */
  onTheWayToServicePointMarker = require('@/assets/marker-on-the-way-to-service-point.svg');

  /**
   * Property `mapHeight`.
   */
  @Prop({ default: null })
  mapHeight: string;

  /**
   * Property `markerCoordinates`.
   */
  @Prop({ default: null })
  markerCoordinates: ICoordinates;

  /**
   * Property `markerType`.
   */
  @Prop({ default: null })
  markerType: string;

  /**
   * Property `vehicleCoordinates`.
   */
  @Prop({ default: null })
  vehicleCoordinates: ICoordinates;

  /**
   * Property `vehicleType`.
   */
  @Prop({ default: null })
  vehicleType: 'van' | 'moto';

  /**
   * Getter `internalMarkerCoordinates`.
   */
  get internalMarkerCoordinates(): LatLngTuple {
    const { latitude = null, longitude = null } = this.markerCoordinates || {};

    return isNumber(latitude) && isNumber(longitude)
      ? [latitude, longitude]
      : null;
  }

  /**
   * Getter `internalVehicleCoordinates`.
   */
  get internalVehicleCoordinates(): LatLngTuple {
    const { latitude = null, longitude = null } = this.vehicleCoordinates || {};

    return isNumber(latitude) && isNumber(longitude)
      ? [latitude, longitude]
      : null;
  }

  /**
   * Getter `vehicleDirection`.
   */
  get vehicleDirection() {
    const { direction = 0 } = this.vehicleCoordinates || {};

    return direction;
  }

  /**
   * Getter `options`.
   */
  get options(): MapOptions {
    return {
      attributionControl: false,
      center: [0, 0],
      maxBounds: [
        [-90, -180],
        [90, 180]
      ],
      maxBoundsViscosity: 0.5,
      minZoom: 2,
      zoom: 2
    };
  }

  /**
   * Getter `tileUrl`.
   */
  get tileUrl() {
    return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  }

  /**
   * Getter `tileLayerOptions`.
   */
  get tileLayerOptions(): TileLayerOptions {
    return {
      referrerPolicy: 'strict-origin-when-cross-origin'
    };
  }

  /**
   * Getter `deliveryIcon`.
   */
  get deliveryIcon() {
    let iconUrl = null;

    switch (this.markerType) {
      case 'processing':
        iconUrl = this.initialMarker;
        break;

      case 'inServicePoint':
        iconUrl = this.inServicePointMarker;
        break;

      case 'onTheWayToServicePoint':
        iconUrl = this.onTheWayToServicePointMarker;
        break;

      case 'delivered':
        iconUrl = this.successMarker;
        break;

      case 'notDelivered':
        iconUrl = this.unsuccessMarker;
        break;

      default:
        iconUrl = this.defaultMarker;
        break;
    }

    return icon({
      iconAnchor: [32, 49],
      iconSize: [64, 64],
      iconUrl
    });
  }

  /**
   * Getter `vehicleIcon`.
   */
  get vehicleIcon() {
    let vehicleIcon: Icon = null;

    switch (this.vehicleType) {
      case 'van':
        vehicleIcon = icon({
          iconAnchor: [9, 17],
          iconSize: [19, 34],
          iconUrl: require('@/assets/van-simple.svg')
        });
        break;

      case 'moto':
        vehicleIcon = icon({
          iconAnchor: [6, 17],
          iconSize: [12, 34],
          iconUrl: require('@/assets/moto.svg')
        });
        break;

      default:
        vehicleIcon = icon({
          iconAnchor: [9, 17],
          iconSize: [19, 34],
          iconUrl: require('@/assets/van-simple.svg')
        });
        break;
    }

    return vehicleIcon;
  }

  /**
   * Getter `canShowVehicle`.
   */
  get canShowVehicle() {
    let isDone = false;

    switch (this.markerType) {
      case 'delivered':
      case 'notDelivered':
      case 'inServicePoint':
        isDone = true;
        break;
    }

    return !isDone && this.internalVehicleCoordinates;
  }

  /**
   * Hook `beforeDestroy`.
   */
  beforeDestroy() {
    if (this.leafletMap) {
      this.leafletMap.off('movestart', this.handleDisableAutoFit);
    }
  }

  /**
   * Handle map ready.
   */
  handleMapReady(mapObject) {
    this.leafletMap = mapObject;

    this.handleMapGrayFilter();
    this.handleAddEventListeners();
    this.handleAutoFitCoordinates();
  }

  /**
   * Handle map gray filter.
   */
  handleMapGrayFilter() {
    if (!window.hasOwnProperty('LFilter')) {
      window['LFilter'] = LFilter;
    }

    const myFilter = [
      'invert:20%',
      'grayscale:100%',
      'bright:130%',
      'contrast:100%',
      'saturate:0%',
      'sepia:2%'
    ];
    (L.tileLayer as any)
      .colorFilter('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        filter: myFilter,
        id: 'osm.mapnik'
      })
      .addTo(this.leafletMap);
  }

  /**
   * Handle add event listeners.
   */
  handleAddEventListeners() {
    if (this.leafletMap) {
      this.leafletMap.on('movestart', this.handleDisableAutoFit);
    }
  }

  /**
   * Handle disable auto fit.
   */
  handleDisableAutoFit() {
    this.autoFit = false;
  }

  /**
   * Handle enable auto fit.
   */
  handleEnableAutoFit() {
    this.autoFit = true;

    this.handleAutoFitCoordinates();
  }

  /**
   * Handle auto fit coordinates.
   */
  handleAutoFitCoordinates() {
    if (this.leafletMap && this.autoFit) {
      const array: LatLngTuple[] = [];

      if (this.internalMarkerCoordinates) {
        array.push(this.internalMarkerCoordinates);
      }

      if (this.internalVehicleCoordinates) {
        array.push(this.internalVehicleCoordinates);
      }

      if (array.length) {
        // Limit map and animation control.
        if (array.length === 1) {
          this.leafletMap.doubleClickZoom.disable();
          this.leafletMap.dragging.disable();
        } else {
          this.leafletMap.doubleClickZoom.enable();
          this.leafletMap.dragging.enable();
        }

        this.leafletMap.once('zoomend', () => {
          const zoom = this.leafletMap.getZoom();
          this.leafletMap.setMinZoom(zoom);

          const bounds = this.leafletMap.getBounds().pad(0.1);
          this.leafletMap.setMaxBounds(bounds);
        });

        const bounds = new LatLngBounds(array);
        const padding = new Point(64, 64);

        const newZoom = getBoundsZoom(this.leafletMap, bounds, padding);
        const currentZoom = this.leafletMap.getZoom();

        if (newZoom < currentZoom) {
          this.leafletMap.setMinZoom(newZoom);
        }

        // Fly to new coordinates.
        this.leafletMap.flyToBounds(bounds, {
          duration: 0.2,
          noMoveStart: true,
          padding
        });
      }
    }
  }

  /**
   * Handle change internal vehicle coordinates.
   */
  @Watch('internalVehicleCoordinates')
  handleChangeInternalVehicleCoordinates(
    newValue: LatLngTuple,
    oldValue: LatLngTuple
  ) {
    if (!oldValue) {
      setTimeout(() => {
        this.handleAutoFitCoordinates();
      }, 500);
    } else {
      this.handleAutoFitCoordinates();
    }
  }

  /**
   * Handle change map height.
   */
  @Watch('mapHeight')
  handleChangeMapHeight() {
    if (this.leafletMap) {
      this.leafletMap.invalidateSize();
      this.handleAutoFitCoordinates();
    }
  }
}
